import React from "react";
import {InformationCircleIcon} from "@heroicons/react/24/outline";

import {cn} from "lib/utils";

/**
 * A section front's masthead rule.
 *
 * Three lines, stacked, in the order a newspaper uses them:
 *
 *   KICKER      what kind of surface this is
 *   Title       what it is called
 *   Standfirst  one line on what it demonstrates
 *
 * The standfirst used to be a paragraph hidden behind the (i), which meant the
 * one thing that explains a section was the one thing you could not see - and
 * on a phone the tooltip opened 250px off the side of the screen. It is plain
 * text under the title now, so the link between a heading and its explanation
 * is visible rather than discoverable.
 *
 * The (i) is kept for the technical note and only appears in debug mode, where
 * the reader has opted into that layer. Its panel is anchored to the heading's
 * left edge and is full-width on small screens, so it cannot leave the page.
 */
/**
 * `tone` encodes who decided this section, using the system's two voices:
 * masthead red where the paper made the call (a control feed, an editor's
 * rule), blue pencil where the engine did. It means a reader can tell at a
 * glance which parts of the page are personalised without reading a word.
 */
const TONE = {
  editorial: "text-masthead",
  engine: "text-pencil",
};

const SectionHeading = ({
  title,
  kicker,
  standfirst,
  tone = "engine",
  debugNote,
  debugMode,
  action,
  className,
}) => (
  <div className={cn("group relative mb-7", className)}>
    <div className="rule-double flex items-start justify-between gap-4 pt-2.5">
      <div className="min-w-0">
        {kicker ? (
          <p
            className={cn(
              "font-sans text-kicker font-semibold uppercase tracking-[0.09em]",
              TONE[tone] || TONE.engine
            )}
          >
            {kicker}
          </p>
        ) : null}

        <div className="flex items-center gap-1.5">
          <h2 className="font-serif text-h2 font-bold tracking-[-0.016em] text-ink">
            {title}
          </h2>

          {debugMode && debugNote ? (
            <button
              type="button"
              aria-label={`Ranking configuration for ${title}`}
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-pencil transition-colors duration-150 ease-out hover:bg-pencil-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <InformationCircleIcon className="size-4" />
            </button>
          ) : null}
        </div>

        {standfirst ? (
          <p className="mt-1 max-w-[52ch] text-meta text-ink-tertiary">
            {standfirst}
          </p>
        ) : null}
      </div>

      {action ? <div className="shrink-0 pt-1">{action}</div> : null}
    </div>

    {debugMode && debugNote ? (
      <span
        role="tooltip"
        className={cn(
          // Anchored to the heading's left edge and full-width on a phone, so
          // it can never run off the side. `hidden` (not opacity-0) keeps it
          // out of layout, which otherwise widened the page's scrollWidth.
          "hidden group-hover:block group-focus-within:block",
          "animate-overlay-in absolute left-0 top-full z-30 mt-1 w-full sm:w-[22rem]",
          "rounded-md border border-pencil/25 bg-popover p-3",
          "font-mono text-kicker leading-relaxed text-ink-secondary shadow-overlay"
        )}
      >
        {debugNote}
      </span>
    ) : null}
  </div>
);

export default SectionHeading;
